/**
 * Typisierte Endpunkt-Funktionen entsprechend dem REST-API-Contract (Abschnitt 6).
 * Aufrufer im Feature-/Auth-Code nutzen ausschließlich diese Funktionen, nie `fetch` direkt.
 *
 * RESPONSE-FORMAT (gegen das laufende Backend verifiziert, 2026-06-29):
 *  Das Backend kapselt Nutzdaten in benannte Wrapper-Objekte
 *  ({ user }, { matchdays }, { matchday, matches }, { predictions }, { prediction }).
 *  Login/Register liefern { token, user }. Das Unwrapping passiert ausschließlich hier,
 *  damit die aufrufenden Komponenten weiter mit den nackten Domain-Typen arbeiten.
 */
import { api } from './client';
import type {
  AdminAttendanceInput,
  AdminMatchday,
  AdminPlayerPredictionInput,
  AdminPrediction,
  AdminPredictionInput,
  AdminQuestionAnswerInput,
  AttendancePrediction,
  AttendancePredictionInput,
  AuthResponse,
  BonusOverview,
  LoginInput,
  Match,
  Matchday,
  MatchCreateInput,
  MatchdayCreateInput,
  MatchdayOverview,
  MatchdayPatchInput,
  MatchdayRanking,
  MatchPatchInput,
  MatchResultInput,
  MatchResultResponse,
  PasswordChangeInput,
  Player,
  PlayerCreateInput,
  PlayerPatchInput,
  PlayerPrediction,
  PlayerPredictionInput,
  Prediction,
  PredictionInput,
  FeedbackInput,
  FeedbackMessage,
  FeedbackSendResult,
  ProfilePatchInput,
  PushMessage,
  PushMessageInput,
  PushSubscriptionInput,
  Question,
  QuestionAnswer,
  QuestionAnswerInput,
  QuestionCreateInput,
  QuestionPatchInput,
  QuestionsResponse,
  RegisterInput,
  RegisterResponse,
  Standings,
  SyncResult,
  Team,
  TeamInput,
  User,
  UserStatus,
} from './types';

/** Team-Liste für Dropdowns (Bonusfragen), user-auth (§14.2). */
export const teamApi = {
  list: (signal?: AbortSignal) =>
    api.get<{ teams: Team[] }>('/teams', undefined, signal).then((r) => r.teams),
  /** Nur Teams der 3. Liga (ohne reine DFB-Pokal-Gegner) — für den Bonusfragen-Dropdown. */
  listLeague: (signal?: AbortSignal) =>
    api.get<{ teams: Team[] }>('/teams', { kind: 'liga' }, signal).then((r) => r.teams),
};

/** Web Push (Erinnerung 24h vor Tippschluss). */
export const pushApi = {
  /** Öffentlich, kein Auth — der VAPID Public Key ist nicht geheim. */
  vapidPublicKey: (signal?: AbortSignal) =>
    api.get<{ public_key: string }>('/push/vapid-key', undefined, signal).then((r) => r.public_key),
  subscribe: (input: PushSubscriptionInput) => api.put<{ ok: boolean }>('/push/subscribe', input),
  unsubscribe: (endpoint: string) =>
    api.post<{ ok: boolean }>('/push/unsubscribe', { endpoint }),
};

export const authApi = {
  /** §11.2: liefert { user, pending: true }, KEINEN Token (kein Auto-Login). */
  register: (input: RegisterInput) => api.post<RegisterResponse>('/auth/register', input),
  login: (input: LoginInput) => api.post<AuthResponse>('/auth/login', input),
  logout: () => api.post<void>('/auth/logout'),
  me: (signal?: AbortSignal) =>
    api.get<{ user: User }>('/auth/me', undefined, signal).then((r) => r.user),
  /** Mein Profil: Tippname/E-Mail ändern. */
  updateProfile: (input: ProfilePatchInput) =>
    api.patch<{ user: User }>('/auth/me', input).then((r) => r.user),
  /** Mein Profil: Passwort ändern (verlangt das aktuelle Passwort). */
  changePassword: (input: PasswordChangeInput) =>
    api.put<{ ok: boolean }>('/auth/me/password', input),
};

export const matchdayApi = {
  list: (signal?: AbortSignal) =>
    api.get<{ matchdays: Matchday[] }>('/matchdays', undefined, signal).then((r) => r.matchdays),
  matches: (matchdayId: number, signal?: AbortSignal) =>
    api
      .get<{ matchday: Matchday; matches: Match[] }>(
        `/matchdays/${matchdayId}/matches`,
        undefined,
        signal,
      )
      .then((r) => r.matches),
  /** §11.4: Spieltags-Ranking, für alle nach Tippschluss; vorher 423. */
  ranking: (matchdayId: number, signal?: AbortSignal) =>
    api.get<MatchdayRanking>(`/matchdays/${matchdayId}/ranking`, undefined, signal),
  /** §13.1: Kader (Münster-Team dieses Spiels), read-only für Tipper. */
  players: (matchdayId: number, signal?: AbortSignal) =>
    api
      .get<{ players: Player[] }>(`/matchdays/${matchdayId}/players`, undefined, signal)
      .then((r) => r.players),
  /**
   * §13.0: „Wer hat getippt"-Übersicht. Vor Deadline sind Fremd-Tipps `null`
   * (nur has_tipped sichtbar), nach Deadline alle gefüllt.
   */
  overview: (matchdayId: number, signal?: AbortSignal) =>
    api.get<MatchdayOverview>(`/matchdays/${matchdayId}/overview`, undefined, signal),
  /**
   * §14.2: Bonus-Fragen eines Spieltags + eigene Antworten. `solution` + eigene
   * `points` liefert das Backend erst nach Deadline.
   */
  questions: (matchdayId: number, signal?: AbortSignal) =>
    api.get<QuestionsResponse>(`/matchdays/${matchdayId}/questions`, undefined, signal),
  /** §14.2: Bonus-Übersicht (Raster Mitspieler × Fragen), Sichtbarkeitsregel. */
  questionsOverview: (matchdayId: number, signal?: AbortSignal) =>
    api.get<BonusOverview>(`/matchdays/${matchdayId}/questions/overview`, undefined, signal),
};

/** Bonus-Antworten der Tipper (§14.2). Deadline-Lock (423) wie Ergebnistipp. */
export const questionApi = {
  /** Antwort auf eine Frage anlegen/ändern. */
  answer: (questionId: number, input: QuestionAnswerInput) =>
    api
      .put<{ answer: QuestionAnswer }>(`/questions/${questionId}/answer`, input)
      .then((r) => r.answer),
};

/** Gesamttabelle (kumuliert über alle Spieltage + Kategorien, mit Aufschlüsselung). */
export const standingsApi = {
  overall: (signal?: AbortSignal) => api.get<Standings>('/standings', undefined, signal),
};

/** Admin-Endpunkte (Contract §11.3). Alle hinter Admin-Gate (role='admin'). */
export const adminApi = {
  listUsers: (status: UserStatus, signal?: AbortSignal) =>
    api.get<{ users: User[] }>('/admin/users', { status }, signal).then((r) => r.users),
  approveUser: (id: number) =>
    api.post<{ user: User }>(`/admin/users/${id}/approve`).then((r) => r.user),
  disableUser: (id: number) =>
    api.post<{ user: User }>(`/admin/users/${id}/disable`).then((r) => r.user),
  /** Kackhaufen-Auszeichnung setzen/entfernen (erscheint in Tippübersicht + Gesamtübersicht). */
  setKackhaufen: (id: number, kackhaufen: boolean) =>
    api.patch<{ user: User }>(`/admin/users/${id}/kackhaufen`, { kackhaufen }).then((r) => r.user),
  /**
   * Setzt ein neues Zufallspasswort und gibt es EINMALIG zurück (kein
   * Mailversand auf dem Shared-Host) — der Admin gibt es dem Mitglied weiter.
   */
  resetUserPassword: (id: number) =>
    api
      .post<{ user: User; password: string }>(`/admin/users/${id}/reset-password`)
      .then((r) => r.password),

  /** Alle Spieltage inkl. unpublished + Zähler. */
  listMatchdays: (signal?: AbortSignal) =>
    api
      .get<{ matchdays: AdminMatchday[] }>('/admin/matchdays', undefined, signal)
      .then((r) => r.matchdays),
  /**
   * Spiele eines Spieltags für die Admin-Pflege. Eigener Endpunkt OHNE
   * is_published-Filter — der öffentliche /matchdays/{id}/matches antwortet
   * für unveröffentlichte (frisch gesyncte) Spieltage mit 404.
   */
  matchdayMatches: (matchdayId: number, signal?: AbortSignal) =>
    api
      .get<{ matchday: Matchday; matches: Match[] }>(
        `/admin/matchdays/${matchdayId}/matches`,
        undefined,
        signal,
      )
      .then((r) => r.matches),
  patchMatchday: (id: number, input: MatchdayPatchInput) =>
    api.patch<{ matchday: AdminMatchday }>(`/admin/matchdays/${id}`, input).then((r) => r.matchday),

  /** Ergebnis eintragen; Backend bepunktet danach alle Tipps des Spiels. */
  putMatchResult: (matchId: number, input: MatchResultInput) =>
    api.put<MatchResultResponse>(`/admin/matches/${matchId}/result`, input),

  /** Sync anstoßen (OpenLigaDB: 3. Liga + DFB-Pokal, Münster-Spiele). */
  syncFixtures: () => api.post<SyncResult>('/admin/sync/fixtures'),

  /* ---- Manuelle Pflege (§12.2) ---- */

  /** Teams für Gegner-Dropdown. */
  listTeams: (signal?: AbortSignal) =>
    api.get<{ teams: Team[] }>('/admin/teams', undefined, signal).then((r) => r.teams),
  /** Nur Teams der 3. Liga (ohne reine DFB-Pokal-Gegner) — für die Kürzelpflege. */
  listLeagueTeams: (signal?: AbortSignal) =>
    api.get<{ teams: Team[] }>('/admin/teams', { kind: 'liga' }, signal).then((r) => r.teams),
  /** Neuen (manuellen) Gegner anlegen; api_team_id bleibt NULL. */
  createTeam: (input: TeamInput) =>
    api.post<{ team: Team }>('/admin/teams', input).then((r) => r.team),
  /** Kürzel einer Mannschaft setzen (Anzeige in Tippübersicht/Bonus). */
  patchTeam: (id: number, shortName: string) =>
    api.patch<{ team: Team }>(`/admin/teams/${id}`, { short_name: shortName }).then((r) => r.team),

  /** Manuellen Spieltag anlegen. */
  createMatchday: (input: MatchdayCreateInput) =>
    api.post<{ matchday: AdminMatchday }>('/admin/matchdays', input).then((r) => r.matchday),

  /** Spiel manuell anlegen / bearbeiten / löschen. */
  createMatch: (input: MatchCreateInput) =>
    api.post<{ match: Match }>('/admin/matches', input).then((r) => r.match),
  patchMatch: (id: number, input: MatchPatchInput) =>
    api.patch<{ match: Match }>(`/admin/matches/${id}`, input).then((r) => r.match),
  deleteMatch: (id: number) => api.del<void>(`/admin/matches/${id}`),

  /** Tipps eines Mitglieds zu einem Spieltag (zum Vorbefüllen). */
  listPredictions: (userId: number, matchdayId: number, signal?: AbortSignal) =>
    api
      .get<{ predictions: AdminPrediction[] }>(
        '/admin/predictions',
        { user: userId, matchday: matchdayId },
        signal,
      )
      .then((r) => r.predictions),
  /** Tipp im Namen eines Mitglieds anlegen/ändern (bis Deadline; sonst 423). */
  putPrediction: (input: AdminPredictionInput) =>
    api.put<{ prediction: AdminPrediction }>('/admin/predictions', input).then((r) => r.prediction),

  /* ---- Kader-Verwaltung (§13.1) ---- */

  /** Kader eines Teams (Münster). */
  listPlayers: (teamId: number, signal?: AbortSignal) =>
    api
      .get<{ players: Player[] }>('/admin/players', { team: teamId }, signal)
      .then((r) => r.players),
  createPlayer: (input: PlayerCreateInput) =>
    api.post<{ player: Player }>('/admin/players', input).then((r) => r.player),
  patchPlayer: (id: number, input: PlayerPatchInput) =>
    api.patch<{ player: Player }>(`/admin/players/${id}`, input).then((r) => r.player),
  deletePlayer: (id: number) => api.del<void>(`/admin/players/${id}`),

  /* ---- Bonusfragen-Pflege (§14.2) ---- */

  /** Fragen eines (Bonus-)Spieltags inkl. correct_*. */
  listQuestions: (matchdayId: number, signal?: AbortSignal) =>
    api
      .get<{ questions: Question[] }>(`/admin/matchdays/${matchdayId}/questions`, undefined, signal)
      .then((r) => r.questions),
  createQuestion: (matchdayId: number, input: QuestionCreateInput) =>
    api
      .post<{ question: Question }>(`/admin/matchdays/${matchdayId}/questions`, input)
      .then((r) => r.question),
  /** Frage bearbeiten; setzt man eine correct-Antwort, bewertet das Backend neu. */
  patchQuestion: (id: number, input: QuestionPatchInput) =>
    api.patch<{ question: Question }>(`/admin/questions/${id}`, input).then((r) => r.question),
  deleteQuestion: (id: number) => api.del<void>(`/admin/questions/${id}`),
  /** §14.2: Bonus-Antwort im Namen eines Mitglieds (bis Deadline; sonst 423). */
  putQuestionAnswer: (questionId: number, input: AdminQuestionAnswerInput) =>
    api
      .put<{ answer: QuestionAnswer }>(`/admin/questions/${questionId}/answer`, input)
      .then((r) => r.answer),

  /* ---- Nacherfassung neue Kategorien (§13.4) ---- */

  /**
   * §13.0/§13.4: Zuschauertipp im Namen eines Mitglieds (bis Deadline; sonst 423).
   * Blinder Schreibpfad — KEINE Vorbefüllung fremder Tipps vor Deadline.
   */
  putAttendance: (matchdayId: number, input: AdminAttendanceInput) =>
    api
      .put<{ attendance_prediction: AttendancePrediction }>(
        `/admin/matchdays/${matchdayId}/attendance`,
        input,
      )
      .then((r) => r.attendance_prediction),
  /** §13.4: Torschützen-Tipp im Namen eines Mitglieds. */
  putScorer: (matchId: number, input: AdminPlayerPredictionInput) =>
    api
      .put<{ scorer_prediction: PlayerPrediction }>(`/admin/matches/${matchId}/scorer`, input)
      .then((r) => r.scorer_prediction),
  /** §13.4: Assist-Tipp im Namen eines Mitglieds. */
  putAssist: (matchId: number, input: AdminPlayerPredictionInput) =>
    api
      .put<{ assist_prediction: PlayerPrediction }>(`/admin/matches/${matchId}/assist`, input)
      .then((r) => r.assist_prediction),

  /* ---- Push-Erinnerung (Migration 017) ---- */

  /** Aktueller Titel/Text der automatischen 24h-Erinnerung. */
  getPushMessage: (signal?: AbortSignal) =>
    api.get<{ message: PushMessage }>('/admin/push/message', undefined, signal).then((r) => r.message),
  /** Titel/Text ändern — wirkt ab dem nächsten Cron-Lauf, kein Deploy nötig. */
  putPushMessage: (input: PushMessageInput) =>
    api.put<{ message: PushMessage }>('/admin/push/message', input).then((r) => r.message),

  /* ---- Feedback (Migration 018) ---- */

  /** Alle Feedback-Nachrichten, neueste zuerst — Sicherheitsnetz zur Mail. */
  listFeedback: (signal?: AbortSignal) =>
    api.get<{ messages: FeedbackMessage[] }>('/admin/feedback', undefined, signal).then((r) => r.messages),
};

export const feedbackApi = {
  /** Freies Feedback an die Admins; bei anonymous=true wird der Absender in Mail/Admin-Ansicht ausgeblendet. */
  send: (input: FeedbackInput) => api.post<FeedbackSendResult>('/feedback', input),
};

export const predictionApi = {
  /** Eigene Tipps zu einem Spieltag: GET /api/predictions?matchday={id} */
  listForMatchday: (matchdayId: number, signal?: AbortSignal) =>
    api
      .get<{ predictions: Prediction[] }>('/predictions', { matchday: matchdayId }, signal)
      .then((r) => r.predictions),
  /** Tipp anlegen/ändern: PUT /api/predictions/{matchId} */
  upsert: (matchId: number, input: PredictionInput) =>
    api.put<{ prediction: Prediction }>(`/predictions/${matchId}`, input).then((r) => r.prediction),
};

/** Neue Tipp-Kategorien (Contract §13.4). Deadline-Lock (423) wie Ergebnistipp. */
export const categoryPredictionApi = {
  /** §13.4: Zuschauertipp (pro Spieltag genau einer). */
  putAttendance: (matchdayId: number, input: AttendancePredictionInput) =>
    api
      .put<{ attendance_prediction: AttendancePrediction }>(
        `/matchdays/${matchdayId}/attendance`,
        input,
      )
      .then((r) => r.attendance_prediction),
  /** §13.4: Torschützen-Tipp (pro Spiel, player_id|null). */
  putScorer: (matchId: number, input: PlayerPredictionInput) =>
    api
      .put<{ scorer_prediction: PlayerPrediction }>(`/matches/${matchId}/scorer`, input)
      .then((r) => r.scorer_prediction),
  /** §13.4: Assist-Tipp (pro Spiel, player_id|null). */
  putAssist: (matchId: number, input: PlayerPredictionInput) =>
    api
      .put<{ assist_prediction: PlayerPrediction }>(`/matches/${matchId}/assist`, input)
      .then((r) => r.assist_prediction),
};
